import type { SpecType } from '@local/lib';
import type { StateCreator } from 'zustand';

import type { QualityFormulaStore } from '../qualityFormulaStore';

import {
  CONTRACT_PRICE,
  CONTRACT_UNIT_PER_MODE,
  getInitialTestComponentValues,
  SPEC_TYPES,
} from '../../qualityFormulaMetadata';

/** Starts the playground's Delivered Quantity at one load for the example contract's mode (see
 * Contract Setup's "Units per Mode"), rather than an unrelated figure. */
const INITIAL_DELIVERED_QUANTITY = CONTRACT_UNIT_PER_MODE;
/** Starts the playground's Starting Price from the same example contract shown in Contract Setup,
 * rather than an unrelated figure. */
const INITIAL_TRADE_PRICE = CONTRACT_PRICE;

const getInitialActuals = (): Record<SpecType, number> =>
  SPEC_TYPES.reduce(
    (acc, spec) => {
      acc[spec.key] = 0;
      return acc;
    },
    {} as Record<SpecType, number>,
  );

export interface TestValuesSlice {
  actualValues: Record<SpecType, number>;
  /** Blanks out specific specs' actual values — used when they're deselected in Step 1's filter (see `specFormSlice`). */
  clearActualValues: (specTypes: SpecType[]) => void;
  deliveredQuantity: number;
  /** Restores every test value back to its starting default — used by `handleResetInputs`. */
  resetTestValues: () => void;
  setActualValues: (updater: (current: Record<SpecType, number>) => Record<SpecType, number>) => void;
  setDeliveredQuantity: (value: number) => void;
  /** Edits one Price Component's playground-only value — see `resolveTestPriceComponentsTotal`. */
  setTestComponentValue: (key: string, value: number) => void;
  /** Edits one spec's playground-only Deemed Price override — never writes back to that spec's
   * real `priceBasis.deemedPrice` (see `testDeemedPriceOverrides`). */
  setTestDeemedPriceOverride: (specType: SpecType, value: number) => void;
  setTradePrice: (value: number) => void;
  /** Playground-editable override of `CONTRACT_PRICE_COMPONENTS`' fixed catalog values, keyed the
   * same way — see `resolveTestPriceComponentsTotal`. */
  testComponentValues: Record<string, number>;
  /** Playground-only override of a spec's Deemed Price, keyed by spec type — absent until edited
   * here, so the playground falls back to displaying that spec's real `priceBasis.deemedPrice`
   * (see FormulaTestingPanel) without ever mutating it. Testing a formula must never change what's
   * actually configured for a spec, only the reverse. */
  testDeemedPriceOverrides: Partial<Record<SpecType, number>>;
  tradePrice: number;
}

/** Owns Step 2's test-value inputs (actuals, delivered quantity, starting price) — independent of Step 1's formula configuration (see `specFormSlice`). */
export const createTestValuesSlice: StateCreator<QualityFormulaStore, [], [], TestValuesSlice> = set => ({
  actualValues: getInitialActuals(),
  clearActualValues: specTypes => {
    if (specTypes.length === 0) return;
    set(state => {
      const actualValues = { ...state.actualValues };
      specTypes.forEach(specType => {
        actualValues[specType] = 0;
      });
      return { actualValues };
    });
  },
  deliveredQuantity: INITIAL_DELIVERED_QUANTITY,

  resetTestValues: () => {
    set({
      actualValues: getInitialActuals(),
      deliveredQuantity: INITIAL_DELIVERED_QUANTITY,
      testComponentValues: getInitialTestComponentValues(),
      testDeemedPriceOverrides: {},
      tradePrice: INITIAL_TRADE_PRICE,
    });
  },

  setActualValues: updater => {
    set(state => ({ actualValues: updater(state.actualValues) }));
  },

  setDeliveredQuantity: value => {
    set({ deliveredQuantity: value });
  },

  setTestComponentValue: (key, value) => {
    set(state => ({ testComponentValues: { ...state.testComponentValues, [key]: value } }));
  },

  setTestDeemedPriceOverride: (specType, value) => {
    set(state => ({ testDeemedPriceOverrides: { ...state.testDeemedPriceOverrides, [specType]: value } }));
  },

  setTradePrice: value => {
    set({ tradePrice: value });
  },

  testComponentValues: getInitialTestComponentValues(),

  testDeemedPriceOverrides: {},

  tradePrice: INITIAL_TRADE_PRICE,
});
